import { NextRouter, useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import { RootState } from '../../store/store';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { post } from '../utils/axiosFetcher';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { DataOrigin } from '../utils/ucpmHelper';
import { getCookie } from '../utils/enrollmentUtil';
import { logger } from '../../logger';
import isClinicalTrialSource from '../../lib/utils/sourceHelper';

const CLINICAL_RESEARCH_OPPORTUNITIES = 'Clinical Research Opportunities';

const useUpdateUCPMJobRecord = (route: any): any => {
  const { sitecoreContext } = useSitecoreContext();
  const isUCPMCallDone = useRef<boolean>(false);
  const siteSettings: any = sitecoreContext?.siteSettings;
  const sourceApp = siteSettings?.source?.fields?.name?.value || 'CR';
  const potentialId = useSelector(
    (state: RootState) => state.potentialParticipantDetails.potentialId
  );
  const cdpState = (sitecoreContext?.CDP as any)?.confirmedProtocolInfo || {};
  const potentialIdFromCDP = (sitecoreContext?.CDP as any)?.potentialId;
  const cdpData = useSelector((state: RootState) => state?.cdpData);
  const contactInfo = useSelector((state: RootState) => state.contactInfoDetails.contactInfo);
  const getOtSponsorId = (isDNQ: any, isNoSite: any, isSuccessPage: any) => {
    if (isDNQ) {
      //single protocol case for DNQ
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.otSponsorId?.value;
      }
      //multiple protocol case for DNQ
      return (sitecoreContext?.siteSettings as any)?.otSponsorId?.value;
    } else if (isNoSite) {
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.otSponsorId?.value;
      }
      return (sitecoreContext?.siteSettings as any)?.otSponsorId?.value;
    } else if (isSuccessPage) {
      return cdpState?.otSponsorId || (sitecoreContext?.siteSettings as any)?.otSponsorId?.value;
    }
  };
  const getParentSponsorMDMID = (isDNQ: any, isNoSite: any, isSuccessPage: any) => {
    if (isDNQ) {
      //single protocol case for DNQ
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields
          ?.parentSponsorMDMEntityId?.value;
      }
      //multiple protocol case for DNQ
      return (sitecoreContext?.siteSettings as any)?.parentSponsorMDMEntityId?.value;
    } else if (isNoSite) {
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields
          ?.parentSponsorMDMEntityId?.value;
      }
      return (sitecoreContext?.siteSettings as any)?.parentSponsorMDMEntityId?.value;
    } else if (isSuccessPage) {
      return (
        cdpState?.parentSponsorMDMID ||
        (sitecoreContext?.siteSettings as any)?.parentSponsorMDMEntityId?.value
      );
    }
  };

  const getProtocolMDMID = (isDNQ: any, isNoSite: any, isSuccessPage: any) => {
    if (isDNQ) {
      //single protocol case for DNQ
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.protocolMdmEntityId
          ?.value;
      }
      //multiple protocol case for DNQ
      return (sitecoreContext?.siteSettings as any)?.parentSponsorMDMEntityId?.value;
    } else if (isNoSite) {
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.protocolMdmEntityId
          ?.value;
      }
      return (sitecoreContext?.siteSettings as any)?.parentSponsorMDMEntityId?.value;
    } else if (isSuccessPage) {
      return (
        cdpState?.protocolMDMID ||
        (sitecoreContext?.siteSettings as any)?.parentSponsorMDMEntityId?.value
      );
    }
  };

  const getSponsorName = (isDNQ: any, isNoSite: any, isSuccessPage: any) => {
    if (isDNQ) {
      //single protocol case for DNQ
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.sponsorName?.value;
      }
      //multiple protocol case for DNQ
      return (sitecoreContext?.siteSettings as any)?.sponsorName?.value;
    } else if (isNoSite) {
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.sponsorName?.value;
      }
      return (sitecoreContext?.siteSettings as any)?.sponsorName?.value;
    } else if (isSuccessPage) {
      return cdpState?.ucpmSponsorName;
    } else {
      return (sitecoreContext?.siteSettings as any)?.sponsorName?.value;
    }
  };

  const getStudyName = (isDNQ: any, isNoSite: any, isSuccessPage: any) => {
    if (isDNQ) {
      //single protocol case for DNQ
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        if ((sitecoreContext?.siteSettings as any)?.studyName?.value) {
          return (sitecoreContext?.siteSettings as any)?.studyName?.value;
        }
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.protocolId?.value;
      }
      //multiple protocol case for DNQ
      return CLINICAL_RESEARCH_OPPORTUNITIES;
    } else if (isNoSite) {
      //single protocol case
      if ((sitecoreContext?.siteSettings as any)?.protocols?.length === 1) {
        if ((sitecoreContext?.siteSettings as any)?.studyName?.value) {
          return (sitecoreContext?.siteSettings as any)?.studyName?.value;
        }
        return (sitecoreContext?.siteSettings as any)?.protocols[0]?.fields?.protocolId?.value;
      }
      //multiple protocol case
      return CLINICAL_RESEARCH_OPPORTUNITIES;
    } else if (isSuccessPage) {
      if ((sitecoreContext?.siteSettings as any)?.studyName?.value) {
        return (sitecoreContext?.siteSettings as any)?.studyName?.value;
      }
      return cdpState?.protocol || '';
    } else {
      return (sitecoreContext?.siteSettings as any)?.studyName?.value;
    }
  };

  const updateUCPMJobRecords = async ({ isDNQ, isNoSite, isSuccessPage }: any) => {
    const payload: any = {
      otSponsorId:
        isClinicalTrialSource(sourceApp) && !isSuccessPage
          ? ''
          : getOtSponsorId(isDNQ, isNoSite, isSuccessPage),
      parentSponsorMDMID:
        isClinicalTrialSource(sourceApp) && !isSuccessPage
          ? ''
          : getParentSponsorMDMID(isDNQ, isNoSite, isSuccessPage),
      protocolMDMID:
        isClinicalTrialSource(sourceApp) && !isSuccessPage
          ? ''
          : getProtocolMDMID(isDNQ, isNoSite, isSuccessPage),
      sponsor: getSponsorName(isDNQ, isNoSite, isSuccessPage),
      iqviaSponsorId: (sitecoreContext?.siteSettings as any)?.iqviaSponsorId?.value,
      guestRef: null, //getCookie('bx_guest_ref'), as per discussion with Manish, this only needed for CR case
      potentialId: potentialId || potentialIdFromCDP,
      dataOrigin: isClinicalTrialSource(sourceApp) ? DataOrigin.TAEPR : DataOrigin.EPR,
      UCPMStudyName:
        isClinicalTrialSource(sourceApp) && !isSuccessPage
          ? ''
          : getStudyName(isDNQ, isNoSite, isSuccessPage),
      integrationAttemptHistory: {
        baseUrl: (sitecoreContext?.siteSettings as any)?.baseUrl?.value,
        isHumanApienabled: false, //this field is not needed for now by UCPM
        tenantKey: (sitecoreContext?.siteSettings as any)?.tenantKey?.value,
        enrollmentId:
          getCookie('enrollmentID') ||
          (sessionStorage.getItem('enrollmentID_for_human_api') as string) ||
          '',
        potentialId: potentialId || potentialIdFromCDP,
        patientId: contactInfo.patientId ?? null,
      },
    };

    if (!(sitecoreContext?.siteSettings as any)?.studyName?.value) {
      logger.info(`${process.env.NEXT_PUBLIC_BASE_URL}/UCPM/ACJMPRUbcdddeoor`, {
        respose: 'StudyName is missing from site settings',
        payload: {},
      });
    }

    return await post(`/UCPM/AddUCPMJobRecord`, payload);
  };

  const { mutate: updateUCPMJob } = useMutation('update-ucpm-jobs-records', updateUCPMJobRecords, {
    onSuccess: () => {
      //console.log('success UCPM data updated');
    },
    onError: () => {
      //console.log('error UCPM data updated');
    },
  });
  const router = useRouter();

  const getReferralStatus = (router: NextRouter) => {
    //for all DNQ cases patientReferralStatus will be "DNQ"
    const isDNQ = cdpData?.patientReferralStatus === 'DNQ' ? true : false;
    const isNoSite =
      ['No-site-available', 'Site-not-found', 'No-site-convenient', 'Site-not-convenient'].includes(
        cdpData?.patientReferralStatus
      ) ||
      cdpData?.patientReferralStatus?.includes('No-site-available-Max-Capacity') ||
      false;
    const isSuccessPage = router.asPath.includes('siteselectionsuccess') ? true : false;
    return { isDNQ, isNoSite, isSuccessPage };
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  useEffect(() => {
    //checking UCPM is enabled or not from sitecore
    const enableUCPMIntegration = (sitecoreContext?.siteSettings as any)?.enableUCPMIntegration
      ?.value as boolean;

    const isOutcomePage = (route?.fields?.isOutcomePage as Field)?.value;
    if (
      isOutcomePage === true &&
      enableUCPMIntegration &&
      router?.isReady &&
      !isUCPMCallDone.current
    ) {
      if (isClinicalTrialSource(sourceApp)) {
        //TA epr scenario
        const data = getReferralStatus(router);
        isUCPMCallDone.current = true;
        setTimeout(() => {
          updateUCPMJob(data);
        }, 1500);
      } else {
        //Independent Sponser epr scenario
        const data = getReferralStatus(router);
        isUCPMCallDone.current = true;
        setTimeout(() => {
          updateUCPMJob(data);
        }, 1500);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.isReady, cdpState]);
};

export default useUpdateUCPMJobRecord;
